import { apiGet, apiPost } from "./axiosClient";
import type { RefundRequest, RefundStatus } from "@/types/domain";

/**
 * React Query keys — bump the suffix when the response handling changes so
 * stale cache (e.g. raw Spring Page) is not reused across deploys.
 */
export const MY_REFUNDS_QUERY_KEY = ["my-refunds", "v1"] as const;
export const ADMIN_REFUNDS_QUERY_KEY = ["admin-refunds", "v1"] as const;

function readString(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

function readNumber(v: unknown): number | undefined {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function coerceRefund(raw: unknown): RefundRequest | null {
  if (raw == null || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const id = readString(o.id);
  const paymentId = readString(o.paymentId);
  const appointmentId = readString(o.appointmentId);
  const patientId = readString(o.patientId);
  const doctorId = readString(o.doctorId);
  const amountPaise = readNumber(o.amountPaise);
  const status = readString(o.status);
  if (
    !id ||
    !paymentId ||
    !appointmentId ||
    !patientId ||
    !doctorId ||
    amountPaise == null ||
    !status
  ) {
    return null;
  }

  const normalizedStatus: RefundStatus =
    status === "PENDING" ||
    status === "PROCESSED" ||
    status === "REJECTED" ||
    status === "FAILED"
      ? (status as RefundStatus)
      : "PENDING";

  return {
    id,
    paymentId,
    appointmentId,
    patientId,
    doctorId,
    amountPaise,
    currency: readString(o.currency) ?? "INR",
    status: normalizedStatus,
    razorpayPaymentId: readString(o.razorpayPaymentId),
    razorpayRefundId: readString(o.razorpayRefundId),
    rejectionReason: readString(o.rejectionReason),
    failureReason: readString(o.failureReason),
    decidedBy: readString(o.decidedBy),
    requestedAt: readString(o.requestedAt),
    decidedAt: readString(o.decidedAt),
    processedAt: readString(o.processedAt),
  };
}

/** Spring `Page<RefundRequestDto>` / `{data: …}` / plain array — always return an array. */
export function normalizeRefundList(data: unknown, depth = 0): RefundRequest[] {
  if (data == null || depth > 4) return [];
  if (Array.isArray(data)) {
    return data
      .map((i) => coerceRefund(i))
      .filter((r): r is RefundRequest => r != null);
  }
  if (typeof data !== "object") return [];
  const o = data as Record<string, unknown>;
  if (Array.isArray(o.content)) {
    return o.content
      .map((i) => coerceRefund(i))
      .filter((r): r is RefundRequest => r != null);
  }
  if ("data" in o) {
    return normalizeRefundList(o.data, depth + 1);
  }
  return [];
}

/** Patient: my refund history (any status). */
export const listMyRefunds = async () => {
  const data = await apiGet<unknown>("/api/payments/refunds/mine");
  return normalizeRefundList(data);
};

/** Admin: refund queue. Pass no status to see *all* history. */
export const listRefundsForAdmin = async (status?: RefundStatus) => {
  const data = await apiGet<unknown>("/api/payments/refunds/admin", {
    params: status ? { status } : undefined,
  });
  return normalizeRefundList(data);
};

export const approveRefund = async (refundId: string) => {
  const data = await apiPost<unknown>(
    `/api/payments/refunds/admin/${refundId}/approve`,
  );
  const r = coerceRefund(data);
  if (!r) throw new Error("Unexpected refund response from server.");
  return r;
};

export const rejectRefund = async (refundId: string, reason: string) => {
  const data = await apiPost<unknown>(
    `/api/payments/refunds/admin/${refundId}/reject`,
    { reason },
  );
  const r = coerceRefund(data);
  if (!r) throw new Error("Unexpected refund response from server.");
  return r;
};
